/**
 * useParallax — subtle scroll-linked translate for hero / infographic images.
 *
 * Returns a ref + an inline style object. The element translates
 * vertically by `intensity` × scrollProgress, clamped between -maxPx and
 * +maxPx so it never drifts off-screen.
 *
 * Uses requestAnimationFrame + a single throttled scroll listener so it
 * stays under 1ms per frame even on low-end Android.
 *
 * Auto-disabled when prefers-reduced-motion is set OR window width < 640
 * (mobile — too small to benefit + battery cost).
 *
 *   const { ref, style } = useParallax({ intensity: 0.12, maxPx: 24 });
 *   <div ref={ref} style={style}><img ... /></div>
 */
import { useEffect, useRef, useState } from "react";

export default function useParallax({ intensity = 0.1, maxPx = 24 } = {}) {
    const ref = useRef(null);
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        // Bail on reduced-motion or small screens
        if (
            typeof window === "undefined" ||
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ||
            window.innerWidth < 640
        ) {
            return;
        }
        let raf = 0;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const rect = el.getBoundingClientRect();
                const vh = window.innerHeight;
                // Center of viewport at 0; clamp progress to [-1, 1]
                const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
                const px = Math.max(
                    -maxPx,
                    Math.min(maxPx, progress * intensity * 100),
                );
                setOffset(px);
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [intensity, maxPx]);

    return {
        ref,
        style: { transform: `translate3d(0, ${offset}px, 0)` },
        className: "reveal-parallax",
    };
}
